import { useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, AlertCircle, CheckCircle, ArrowRight, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * Shared page for both `/portal/reset` and `/portal/accept`.
 *   /portal/reset?token=...    — resets an existing password (posts to /client/auth/reset)
 *   /portal/accept?token=...   — accepts an admin invite (posts to /client/auth/accept-invite)
 * Both flows collect a new password and either redirect to /client-portal (accept
 * auto-signs in) or /client-portal (reset just shows a success message).
 */
export default function PortalPasswordPage({ mode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const isReset = mode === "reset";
  const endpoint = isReset ? "/client/auth/reset" : "/client/auth/accept-invite";
  const body = isReset ? { token, new_password: password } : { token, password };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await axios.post(`${API}${endpoint}`, body);
      setDone(true);
      // Accept flow auto-signs in — send them to the portal
      if (!isReset) setTimeout(() => navigate("/client-portal", { replace: true }), 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#05050a] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center rounded-3xl bg-[#0a0510] border border-[#ff0055]/40 p-8">
          <AlertCircle size={26} className="text-[#ff0055] mx-auto mb-4" />
          <h1 className="font-display font-black text-2xl">Link is missing a token</h1>
          <p className="text-[#a395c3] text-sm mt-3">
            Ask the studio to send a fresh {isReset ? "reset" : "invite"} link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050a] text-white flex items-center justify-center px-4 py-10">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full blur-[120px] bg-[#00f0ff]/10" />
      </div>
      <div className="relative w-full max-w-md rounded-3xl bg-[#0a0510] border border-white/10 p-6 md:p-8">
        <a href="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50 hover:text-white mb-6">
          SB Media Studio
        </a>

        {done && isReset && (
          <div className="text-center py-6" data-testid="portal-reset-success">
            <CheckCircle size={26} className="text-[#22c55e] mx-auto mb-4" />
            <h1 className="font-display font-black text-2xl">Password updated.</h1>
            <p className="text-[#a395c3] text-sm mt-3">
              Sign in with your new password to open the portal.
            </p>
            <button
              onClick={() => navigate("/client-portal")}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-white transition-colors"
            >
              Go to sign in <ArrowRight size={14} />
            </button>
          </div>
        )}

        {done && !isReset && (
          <div className="text-center py-6" data-testid="portal-accept-success">
            <CheckCircle size={26} className="text-[#22c55e] mx-auto mb-4" />
            <h1 className="font-display font-black text-2xl">You&apos;re in.</h1>
            <p className="text-[#a395c3] text-sm mt-3">Redirecting you to the portal…</p>
          </div>
        )}

        {!done && (
          <>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00f0ff] mb-2">
              {isReset ? "Reset password" : "Accept invite"}
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight leading-tight mb-2">
              {isReset ? "Pick a new password." : "Set your password."}
            </h1>
            <p className="text-[#a395c3] text-sm mb-6">
              {isReset
                ? "This link expires in 60 minutes. Once you set a new password, all existing sessions sign out."
                : "The studio invited you — pick a password to open your portal."}
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] flex items-center gap-2 mb-1.5">
                  <Lock size={11} /> New password · min 8 chars
                </label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    autoComplete="new-password"
                    data-testid="portal-password-input"
                    className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 pr-12 text-sm focus:outline-none focus:border-[#00f0ff]/60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md hover:bg-white/[0.06] flex items-center justify-center"
                  >
                    {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
              </div>

              {error && (
                <div role="alert" data-testid="portal-password-error" className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#ff0055]/10 border border-[#ff0055]/40 text-[#ff0055] text-xs">
                  <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="portal-password-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-white transition-colors disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {isReset ? "Update password" : "Set password & sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
